/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductDimensionsWithAvailabilitiesFull } from '@msdyn365-commerce/commerce-entities';
import { ProductDimensionsMatrix } from '@msdyn365-commerce/components';
import { RichTextComponent } from '@msdyn365-commerce/core';
import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { ArrayExtensions, IDimensionsApp } from '@msdyn365-commerce-modules/retail-actions';
import { Button, Collapse, IModuleProps, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IBulkPurchaseData } from './bulk-purchase.data';
import { IBulkPurchaseProps } from './bulk-purchase.props.autogenerated';

/**
 * Additional optional props for the bulk purchase.
 */
export interface IBulkPurchaseExtendedProps extends IBulkPurchaseProps<IBulkPurchaseData> {
    productDimensions?: IProductDimensionsWithAvailabilitiesFull[];
}

/**
 * View for the bulk purchase module.
 */
export interface IBulkPurchaseViewProps extends IBulkPurchaseProps<IBulkPurchaseData> {
    container: IModuleProps;
    content: React.ReactElement;
}

/**
 * View for the bulk purchase module.
 */
export interface IBulkPurchaseState {
    isExpanded: boolean;
}

/**
 * Bulk-purchase module.
 */
export class BulkPurchase extends React.PureComponent<IBulkPurchaseExtendedProps, IBulkPurchaseState> {
    public static readonly className = 'ms-bulk-purchase';

    public constructor(props: IBulkPurchaseExtendedProps) {
        super(props);

        this.state = {
            isExpanded: false
        };
    }

    public render(): React.ReactElement | null {
        const hasDimensionsInProps = ArrayExtensions.hasElements(this.props.productDimensions);
        const hasDimensionsInData = ArrayExtensions.hasElements(this.props.data.productDimensions?.result);
        if (!hasDimensionsInProps && !hasDimensionsInData) {
            return null;
        }

        const viewProps: IBulkPurchaseViewProps = {
            ...this.props,
            container: {
                moduleProps: this.props,
                className: classnames(BulkPurchase.className, this.props.config.className)
            },
            content: this._renderCollapseContent(`${BulkPurchase.className}__content`)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderCollapseContent(className: string): React.ReactElement {
        return (
            <div className={className}>
                {this._renderCollapseButton(`${className}__collapse-button`)}
                {this._renderCollapse(`${className}__collapse`)}
            </div>
        );
    }

    private _renderCollapseButton(className: string): React.ReactElement {
        return (
            <Button
                className={className}
                onClick={this._toggleCollapse}
                aria-expanded={this.state.isExpanded}
                title={this.props.resources.bulkPurchaseHeaderTitle}
            >
                {this.props.resources.bulkPurchaseHeaderTitle}
            </Button>
        );
    }

    private _renderCollapse(className: string): React.ReactElement {
        return (
            <Collapse className={className} isOpen={this.state.isExpanded}>
                {this._renderMainContent(`${className}__main-content`)}
            </Collapse>
        );
    }

    private _renderMainContent(className: string): React.ReactElement {
        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';

        return (
            <div className={className}>
                {!isMobileDevice && (
                    <div className={`${className}__description`}>
                        <RichTextComponent text={this.props.resources.bulkPurchaseDescriptionText} />
                    </div>
                )}
                {this._renderMatrix(`${className}__matrix-container`)}
            </div>
        );
    }

    private _renderMatrix(className: string): React.ReactElement {
        const hasDimensionsInProps = ArrayExtensions.hasElements(this.props.productDimensions);

        return (
            <ProductDimensionsMatrix
                className={className}
                context={this.props.context as ICoreContext<IDimensionsApp>}
                data={{
                    dimensions: hasDimensionsInProps ? this.props.productDimensions! : this.props.data.productDimensions!.result!,
                    orderTemplates: this.props.data.orderTemplates?.result,
                    customerInformation: this.props.data.customerInformation?.result
                }}
                resources={this.props.resources}
            />
        );
    }

    /**
     * Callback which toggles collapse mode.
     */
    private readonly _toggleCollapse = () => {
        this.setState(state => ({
            isExpanded: !state.isExpanded
        }));
    };
}

export default BulkPurchase;
